import _ from 'underscore';

import {assert} from 'Asserts';
import {removeGaps} from 'biojs/Util';
import {AlignmentPolymerType, ProteinAlignment} from 'constants/AlignmentConstants';
import {getRealToGapMap} from 'molbio/aa-sequences/alignments/getRealToGapMap';
import {getResidueHighlight} from 'molbio/AlignmentUtil';
import AntibodyAnalysisStore from 'molbio/antibody-engineering/antibodyAnalysis';
import {CDRDefinition, ValidCDRResult} from 'molbio/antibody-engineering/AntibodyEngineeringConstants';
import {getAntibodyVisibilitySettings, shouldShowCDRs} from 'molbio/antibody-engineering/visibilityHelpers';
import {AlignmentMapState} from 'molbio/maps/MapStates';
import SequenceAlignmentTabState from 'molbio/tabstates/SequenceAlignmentTabState';
import {VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';

import {
  CDRAnnotationName,
  PDFExportCDRAnnotation,
  PDFExportRawSequences,
  PDFExportResidue,
} from './AlignmentPDFConstants';

interface SequenceInfo {
  residues: string;
  master: boolean;
  name: string;
}

/**
 * Gets relevant sequence data from SequenceAlignmentTabState, sorting master model in front
 */
function extractSequencesFromTabState(tabState: SequenceAlignmentTabState): Array<SequenceInfo> {
  const mapStates = tabState.get('mapstates').models;
  return (
    mapStates
      .map((mapState: AlignmentMapState) => ({
        residues: mapState.get('sequence').bases(),
        master: mapState.isMasterSequence(),
        name: mapState.get('sequence').name(),
      }))
      // Sort master to the front of the array
      .sort((seq1: SequenceInfo, seq2: SequenceInfo) => {
        if (seq1.master) {
          return -1;
        } else if (seq2.master) {
          return 1;
        }
        return 0;
      })
  );
}

/**
 * Extracts data from an SequenceAlignmentTabState to render a PDF
 */
export function generateRawSequencesFromDNAAlignment(
  tabState: SequenceAlignmentTabState
): PDFExportRawSequences {
  const sequences = extractSequencesFromTabState(tabState);
  assert(
    // eslint-disable-next-line no-restricted-syntax
    _.uniq(sequences.map((s: SequenceInfo) => s.residues.length)).length === 1,
    'Cannot generate PDF for alignment with different length sequences'
  );

  const masterSequence = sequences[0];
  assert(Boolean(masterSequence.master), 'Error generating PDF: alignment does not have a master sequence');

  return sequences.map((sequence: SequenceInfo) => {
    if (sequence.master) {
      return {
        sequenceName: sequence.name,
        residues: sequence.residues.split('').map((residue) => ({letter: residue, format: null})),
      };
    }

    return {
      sequenceName: sequence.name,
      residues: sequence.residues.split('').map((letter, i) => {
        const residue: PDFExportResidue = {letter, format: null};
        const residueHighlightFormat = getResidueHighlight(masterSequence.residues, sequence.residues, i);
        if (residueHighlightFormat) {
          residue.format = residueHighlightFormat;
        }
        return residue;
      }),
    };
  });
}

export async function generateRawSequencesFromAaSequenceAlignment(
  alignment: ProteinAlignment,
  visibilityOptions: VisibilityOptions
): Promise<PDFExportRawSequences> {
  const rows = alignment.rows;
  const master = rows[0];
  const ungappedResidues = rows.map((row) => removeGaps(row.proteinVersion.aminoAcids));
  const showCDRs = shouldShowCDRs(visibilityOptions);
  const maybeAnnotations: Array<Array<ValidCDRResult> | null> = showCDRs
    ? await AntibodyAnalysisStore.getCDRAnnotations(
        ungappedResidues,
        getAntibodyVisibilitySettings(visibilityOptions)[1] as CDRDefinition
      )
    : [];

  const cdrAnnotations: Array<Array<PDFExportCDRAnnotation>> = maybeAnnotations.map((anns, i) => {
    if (!anns) {
      return [];
    }
    const map = getRealToGapMap(rows[i].proteinVersion.aminoAcids);
    // eslint-disable-next-line no-restricted-syntax
    const flattenedRegions = _.flatten(anns.map((a) => a.regions));
    return flattenedRegions.map((ann) => ({
      name: ann.name.includes('CDR') ? CDRAnnotationName.CDR : CDRAnnotationName.FRAMEWORK,
      range: {start: map[ann.start], end: map[ann.end]},
    }));
  });

  return rows.map((row, rowIndex) => {
    if (rowIndex === 0) {
      return {
        sequenceName: row.name,
        residues: row.proteinVersion.aminoAcids.split('').map((residue) => ({letter: residue, format: null})),
        cdrAnnotations: showCDRs ? cdrAnnotations[rowIndex] : [],
      };
    }

    return {
      sequenceName: row.name,
      cdrAnnotations: showCDRs ? cdrAnnotations[rowIndex] : [],
      residues: row.proteinVersion.aminoAcids.split('').map((aa, i) => {
        const residue: PDFExportResidue = {letter: aa, format: null};
        const residueHighlightFormat = getResidueHighlight(
          master.proteinVersion.aminoAcids,
          row.proteinVersion.aminoAcids,
          i,
          AlignmentPolymerType.AA
        );
        if (residueHighlightFormat) {
          residue.format = residueHighlightFormat;
        }
        return residue;
      }),
    };
  });
}
